import type { ErrorEvent, Exception } from '@sentry/react';
import { env } from '../config/env';
import { normalizeUrlPath } from './sentryGrouping';

/**
 * Backend availability is monitored server-side, so transport-level failures reaching the
 * browser are duplicate and non-actionable here. They are dropped except for a small sample
 * kept under one fingerprint, which preserves visibility into reachability problems the
 * server-side monitor cannot see (DNS, CDN/edge/WAF, CORS, ad blockers, corporate proxies).
 */
export const BACKEND_UNREACHABLE_FINGERPRINT = 'backend-unreachable';

/**
 * Apollo rethrows every GraphQL error as a `CombinedGraphQLErrors` rejection, so call sites
 * without a catch report it a second time through the global handler. `errorLink` in
 * src/graphql/index.ts already captured each sub-error with the operation, variables, and
 * query, which makes the rejection copy a strictly poorer duplicate. Only a sample is kept,
 * because losing it entirely would hide the missing catch.
 */
export const GRAPHQL_UNHANDLED_FINGERPRINT = 'graphql-unhandled-rejection';

/** Share of matched duplicate or transport events still sent to Sentry. */
export const SAMPLE_RATE = 0.01;

/** HTTP statuses that mean the edge answered but the origin did not. */
const UNAVAILABLE_STATUS_CODES = new Set([0, 502, 503, 504]);

/**
 * Messages browsers emit when a fetch never completed. Chrome/Edge say "Failed to fetch",
 * Firefox "NetworkError when attempting to fetch resource.", Safari "Load failed" or one of
 * its CFNetwork phrasings. Our Apollo error link appends " (host)", hence the prefix match.
 */
const FETCH_FAILURE_MESSAGES = [
    'Failed to fetch',
    'NetworkError when attempting to fetch resource.',
    'Load failed',
    'Network request failed',
    'The Internet connection appears to be offline.',
    'The network connection was lost.',
];

const apiHost = ((): string | undefined => {
    try {
        return new URL(env.backend.url, window.location.origin).host;
    } catch {
        return undefined;
    }
})();

/**
 * The same browser messages are emitted for S3 uploads/downloads, the health poller, and
 * geolocation. Those are a different failure domain that the server-side monitor does not
 * cover, so only failures identifiable as backend API calls are filtered. Our capture sites
 * tag those (`src/graphql/index.ts`, `src/utils/axios.ts`), and the Apollo error link also
 * appends the host to the message.
 */
const isApiTransport = (event: ErrorEvent, { value }: Exception): boolean =>
    Boolean(event.tags?.['graphql.operation']) ||
    Boolean(event.tags?.['http.path']) ||
    Boolean(apiHost && (value ?? '').includes(`(${apiHost})`));

const isFetchTransportFailure = ({ type, value }: Exception): boolean =>
    type === 'TypeError' && FETCH_FAILURE_MESSAGES.some((message) => (value ?? '').startsWith(message));

const isAxiosTransportFailure = ({ type, value }: Exception): boolean =>
    type === 'AxiosError' && value === 'Network Error';

const isUnavailableServerError = ({ type }: Exception, event: ErrorEvent): boolean => {
    if (type !== 'ServerError' && type !== 'ServerParseError') return false;

    const statusCode = event.contexts?.extra?.statusCode;
    return typeof statusCode === 'number' && UNAVAILABLE_STATUS_CODES.has(statusCode);
};

/**
 * A transport failure is only noise when the app handled it. An unhandled rejection means a
 * backend outage escaped into a real frontend crash — a missing ErrorRetry or catch in our
 * code — so those are always kept, including AbortError.
 *
 * `exception.values` is ordered oldest-to-newest: linked-errors prepends each `cause` and the
 * thrown error sits last. Causes are also rewritten to `handled: true`, so the handled flag has
 * to be read across the whole chain and the match against its final entry.
 */
const isDroppableTransportFailure = (event: ErrorEvent): boolean => {
    const values = event.exception?.values;
    if (!values?.length) return false;
    if (values.some(({ mechanism }) => mechanism?.handled === false)) return false;

    const thrown = values[values.length - 1];
    if (!isApiTransport(event, thrown)) return false;

    return (
        isFetchTransportFailure(thrown) ||
        isAxiosTransportFailure(thrown) ||
        isUnavailableServerError(thrown, event)
    );
};

/**
 * The duplicate of a GraphQL error that `errorLink` already reported with full context. The
 * global handler sees the rethrown `CombinedGraphQLErrors`, so dropping it costs no detail
 * beyond the fact that a call site is missing a catch.
 */
const isDuplicateGraphQLRejection = (event: ErrorEvent): boolean => {
    const values = event.exception?.values;
    if (!values?.length) return false;

    const { type, mechanism } = values[values.length - 1];

    return type === 'CombinedGraphQLErrors' && Boolean(mechanism?.type?.endsWith('onunhandledrejection'));
};

const sample = (event: ErrorEvent, fingerprint: string, tag: string): ErrorEvent | null => {
    if (Math.random() >= SAMPLE_RATE) return null;

    event.fingerprint = [fingerprint];
    event.level = 'warning';
    event.tags = {
        ...event.tags,
        [tag]: 'true',
        ...(event.request?.url && { [`${tag}.route`]: normalizeUrlPath(event.request.url) }),
    };

    return event;
};

export const beforeSend = (event: ErrorEvent): ErrorEvent | null => {
    if (isDuplicateGraphQLRejection(event)) {
        return sample(event, GRAPHQL_UNHANDLED_FINGERPRINT, 'graphql_unhandled');
    }

    if (!isDroppableTransportFailure(event)) return event;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;

    return sample(event, BACKEND_UNREACHABLE_FINGERPRINT, 'backend_unreachable');
};
